import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SFTPInputs } from './SFTPInputs';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { useParams } from 'react-router-dom';
import { fetchSftpConfig, setSftpConfig, updateSftpKeys } from '../../store/merchnts/actions/sftp';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { SectionWrapper } from '../SectionWrapper';
import { ButtonComponent } from '../ButtonComponent';

export const SFTPSection = (): JSX.Element => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { merchantState: { sftpConfig, isSftpLoading }, dispatch } = useMerchants();
	const [loading, setLoading] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const [updateModalState, setUpdateModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const [publicKey, setPublicKey] = useState('');
	const [privateKey, setPrivateKey] = useState('');

	useEffect(() => {
		void fetchSftpConfig(dispatch, id);
	}, []);

	const closeModal = (): void => {
		setModalState(prev => ({ ...prev, isOpen: false }));
		setUpdateModalState(prev => ({ ...prev, isOpen: false }));
	};

	const sftpFields = [
		{
			key: 'url',
			name: 'SFTP.URL',
			value: sftpConfig?.url
		},
		{
			key: 'port',
			name: 'SFTP.PORT',
			value: sftpConfig?.port
		},
		{
			key: 'username',
			name: 'SFTP.USERNAME',
			value: sftpConfig?.username
		},
		{
			key: 'password',
			name: 'SFTP.PASSWORD',
			value: sftpConfig?.password
		},
		{
			key: 'privateKey',
			name: 'SSH Private key',
			value: sftpConfig?.privateSshKey ?? 'No SSH key found'
		}
	];

	const openSuccessModal = (): void => (
		setModalState({
			isOpen: true,
			modalTitle: 'Enable SFTP',
			content: (
				<div className="fw-bolder fs-6">
					SFTP was successfully created for this merchant
				</div>
			),
			confirmButtonText: 'Ok',
			onConfirm: closeModal,
			minHeight: 100
		})
	);

	const openFailedModal = (generalError?: string): void => (
		setModalState({
			isOpen: true,
			modalTitle: 'Enable SFTP',
			content: (
				<div className="fw-bolder fs-6">
					{ generalError ? generalError : 'SFTP enabling Failed' }
				</div>
			),
			confirmButtonText: 'Ok',
			onConfirm: closeModal,
			minHeight: 100
		})
	);

	const handleEnable = async (): Promise<void> => {
		setLoading(true);
		try {
			await setSftpConfig(id);
			await fetchSftpConfig(dispatch, id);
			openSuccessModal();
			setLoading(false);

		} catch (e) {
			console.log('e', e.response);
			openFailedModal(e.response?.data?.General?.[0]);
			setLoading(false);
		}
	};

	const getSftpBody = (): JSX.Element => {
		if (sftpConfig?.url
			&& sftpConfig?.port
			&& sftpConfig?.username
			&& sftpConfig?.password
		) {
			return (
				<>
					{sftpFields.map(item => (
						<SFTPInputs
							{...item}
							key={item.key}
						/>
					))}
					<div className="col-12">
						<label className="form-label col-12">
							Public key
							<textarea
								maxLength={250}
								className="form-control form-control-sm form-control-solid resize-none mt-1 h-100px"
								placeholder="Write public key here"
								value={publicKey}
								onChange={(e): void => setPublicKey(e.target.value)}
							/>
						</label>
					</div>
					<div className="col-12">
						<label className="form-label col-12">
							Private key
							<textarea
								maxLength={250}
								className="form-control form-control-sm form-control-solid resize-none mt-1 h-100px"
								placeholder="Write private key here"
								value={privateKey}
								onChange={(e): void => setPrivateKey(e.target.value)}
							/>
						</label>
					</div>
				</>
			);
		} else {
			return (
				<div className="text-center fs-5 mt-7">
					Please, click &apos;Enable&apos; button to setup SFTP client.
				</div>
			);
		}
	};

	const buttons = (
		<>
			{
				((publicKey.length > 3 || privateKey.length > 3) && sftpConfig?.enabled && !isSftpLoading) && (
					<ButtonComponent
						buttonName={t('SFTP.BTN_UPDATE')}
						additionalClass="btn-sm w-100px"
						onButtonClick={(): void => setUpdateModalState({ isOpen: true })}
						loading={loading}
					/>
				)
			}
			{
				(!sftpConfig?.enabled && !isSftpLoading) && (
					<ButtonComponent
						buttonName={t('SFTP.BTN_NAME')}
						additionalClass="btn-sm w-100px"
						onButtonClick={handleEnable}
						loading={loading}
					/>
				)
			}
		</>

	);

	const handleUpdate = (): void => {
		updateSftpKeys(id, publicKey, privateKey, sftpConfig?.publicSshKeyId)
			.then(async (): Promise<void> => {
				await fetchSftpConfig(dispatch, id);
				setLoading(false);
				setUpdateModalState({ isOpen: false });
			})
			.catch(() => {
				setLoading(false);
			});
	};

	return (
		<>
			<SectionWrapper
				title={t('SFTP.SECTION_TITLE')}
				sectionAdditionalClass="mb-5 min-height-200"
				headerAdditionalBlock={buttons}
				isCardBody
				pageLoading={isSftpLoading}
			>
				{getSftpBody()}

			</SectionWrapper>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
			<ModalWindow
				isOpen={updateModalState.isOpen}
				content={
						<div className="fs-6">
							Are you sure you want to update SSH keys?
						</div>
				}
				onClose={closeModal}
				onCancel={closeModal}
				onConfirm={handleUpdate}
				minHeight={100}
			/>
		</>
	);
};
